import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Volume2, Pause, Square, Download, Play, Copy, EyeOff, Eye, Tag, Coins, MoreVertical,
  Trash2, Pencil, RotateCcw, Flag, Bug, Eye as EyeIcon, FileDown,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { CATEGORY_BADGES } from "@/lib/message-classification";
import { toast } from "sonner";
import { useLocale } from "@/hooks/useLocale";
import { ReportContentButton } from "@/components/moderation/ReportContentButton";

interface MessageCardMenuProps {
  msgId: string;
  msgContent: string;
  msgCategory: string;
  canTTS: boolean;
  isThisMsgPlaying: boolean;
  isPaused?: boolean;
  onSpeak: () => void;
  onDownload: () => void;
  onPause?: () => void;
  onResume?: () => void;
  onStop?: () => void;
  onToggleHide?: () => void;
  isHidden?: boolean;
  onReclassify?: (msgId: string, newCategory: string) => void;
  onDelete?: (msgId: string) => void;
  onEdit?: () => void;
  canEdit?: boolean;
  onRetry?: () => void;
  creditsUsed?: number | null;
  /** When true, shows a "Report" item that opens the moderation dialog. */
  canReport?: boolean;
  /** World context for the report (so admins can group by world). */
  worldId?: string | null;
  /** Admin/editor only — opens the message debug visualizer. */
  onDebugVisualize?: () => void;
  /** Admin/editor only — downloads the full message debug log. */
  onDebugDownload?: () => void;
}

export function MessageCardMenu({
  msgId, msgContent, msgCategory, canTTS, isThisMsgPlaying, isPaused,
  onSpeak, onDownload, onPause, onResume, onStop,
  onToggleHide, isHidden, onReclassify, onDelete, onEdit, canEdit,
  onRetry, creditsUsed, canReport, worldId, onDebugVisualize, onDebugDownload,
}: MessageCardMenuProps) {
  const { t } = useLocale();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  // Use a custom trigger inside the dropdown so the report dialog opens from
  // the menu without unmounting the dropdown unexpectedly.
  const [reportOpen, setReportOpen] = useState(false);
  const hasCredits = creditsUsed != null && creditsUsed > 0;

  const handleCopy = () => {
    navigator.clipboard.writeText(msgContent).then(
      () => toast.success(t.gen_copied_clipboard),
      () => toast.error(t.copy_failed),
    );
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-foreground" aria-label={t.aria_message_options}>
            <MoreVertical className="w-3.5 h-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[180px]">
          {canTTS && (
            <>
              {isThisMsgPlaying ? (
                <>
                  <DropdownMenuItem onClick={isPaused ? onResume : onPause} className="text-xs gap-2">
                    {isPaused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
                    {isPaused ? t.common_resume : t.common_pause}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={onStop} className="text-xs gap-2">
                    <Square className="w-3.5 h-3.5" /> Stop
                  </DropdownMenuItem>
                </>
              ) : (
                <DropdownMenuItem onClick={onSpeak} className="text-xs gap-2">
                  <Volume2 className="w-3.5 h-3.5" /> Read out loud
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={onDownload} className="text-xs gap-2">
                <Download className="w-3.5 h-3.5" /> Download audio
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem onClick={handleCopy} className="text-xs gap-2">
            <Copy className="w-3.5 h-3.5" /> Copy text
          </DropdownMenuItem>
          {canReport && (
            <DropdownMenuItem
              onSelect={() => {
                // Defer dialog open by one tick so the dropdown finishes its
                // close animation and releases focus before the dialog claims it.
                setTimeout(() => setReportOpen(true), 0);
              }}
              className="text-xs gap-2"
            >
              <Flag className="w-3.5 h-3.5" /> Report content
            </DropdownMenuItem>
          )}
          {onToggleHide && (
            <DropdownMenuItem onClick={onToggleHide} className="text-xs gap-2">
              {isHidden ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
              {isHidden ? "Show message" : "Hide message"}
            </DropdownMenuItem>
          )}
          {onReclassify && (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="text-xs gap-2">
                <Tag className="w-3.5 h-3.5" /> Reclassify
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="min-w-[140px]">
                {Object.entries(CATEGORY_BADGES).map(([key, cfg]) => (
                  <DropdownMenuItem key={key} className={cn("text-xs gap-2", key === msgCategory && "font-bold")} onClick={() => onReclassify(msgId, key)}>
                    <span>{cfg.label}</span>
                    {key === msgCategory && <span className="ml-auto text-primary">✓</span>}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          )}
          {onRetry && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onRetry} className="text-xs gap-2">
                <RotateCcw className="w-3.5 h-3.5" /> Retry
              </DropdownMenuItem>
            </>
          )}
          {canEdit && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onEdit} className="text-xs gap-2">
                <Pencil className="w-3.5 h-3.5" /> Edit message
              </DropdownMenuItem>
              {onDelete && (
                <DropdownMenuItem onClick={() => setConfirmDeleteOpen(true)} className="text-xs gap-2 text-destructive focus:text-destructive">
                  <Trash2 className="w-3.5 h-3.5" /> Delete message
                </DropdownMenuItem>
              )}
            </>
          )}
          {hasCredits && (
            <>
              <DropdownMenuSeparator />
              <div className="px-2 py-1.5 text-[10px] text-muted-foreground flex items-center gap-1.5 tabular-nums font-mono">
                <Coins className="w-3 h-3 text-amber-500" />
                Credits used: {Number(creditsUsed) < 0.01 ? "<0.01" : Number(creditsUsed).toFixed(4)}
              </div>
            </>
          )}
          {(onDebugVisualize || onDebugDownload) && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="text-xs gap-2">
                  <Bug className="w-3.5 h-3.5" /> Debug
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="min-w-[160px]">
                  {onDebugVisualize && (
                    <DropdownMenuItem onClick={onDebugVisualize} className="text-xs gap-2">
                      <EyeIcon className="w-3.5 h-3.5" /> Visualize
                    </DropdownMenuItem>
                  )}
                  {onDebugDownload && (
                    <DropdownMenuItem onClick={onDebugDownload} className="text-xs gap-2">
                      <FileDown className="w-3.5 h-3.5" /> Log (download)
                    </DropdownMenuItem>
                  )}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {onDelete && (
        <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete message?</AlertDialogTitle>
              <AlertDialogDescription>This message will be permanently deleted.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t.cancel}</AlertDialogCancel>
              <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => onDelete(msgId)}>{t.delete}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      {canReport && (
        <ReportContentButton
          contentType="adventure_message"
          contentId={msgId}
          worldId={worldId ?? null}
          trigger={null}
          open={reportOpen}
          onOpenChange={setReportOpen}
        />
      )}
    </>
  );
}
